/**
 * UserList - Liste des utilisateurs avec filtres avancés et actions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { PUBLIC_APP_URL } from '../../config';
import { useQuery, useMutation } from '@apollo/client';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';

// Queries & Mutations
import {
  GET_ADMIN_USERS,
  GET_ADMIN_USER_GLOBAL_STATS,
  SUSPEND_USER,
  REACTIVATE_USER,
  SET_VERIFIED_BADGE,
  SET_USER_ROLE,
} from './user.queries';

// Icons for new actions
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// Types
interface AdminUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  username: string | null;
  slug: string | null;
  avatarUrl: string | null;
  jobTitle: string | null;
  company: string | null;
  city: string | null;
  country: string | null;
  sector: string | null;
  profileViews: number;
  verifiedBadge: boolean;
  isPremium: boolean;
  accountStatus: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'PENDING';
  role: 'USER' | 'ADMIN' | 'MODERATOR';
  createdAt: string;
  subscription: { plan: string; status: string } | null;
  cardsCount: number;
}

interface GlobalStats {
  total: number;
  active: number;
  suspended: number;
  verified: number;
  newThisMonth: number;
  byRole: Record<string, number>;
}

// Status colors
const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#10B981',
  SUSPENDED: '#EF4444',
  DELETED: '#6B7280',
  PENDING: '#F59E0B',
};

const ROLE_COLORS: Record<string, string> = {
  USER: '#6B7280',
  MODERATOR: '#8B5CF6',
  ADMIN: '#EF4444',
};

// Stats Card Component
function StatsCard({ title, value, icon, color, loading }: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {loading ? <CircularProgress size={20} /> : value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export function UserList() {
  const navigate = useNavigate();
  const notify = useNotify();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  // Debounced search for better UX
  const debouncedSearch = useDebounce(search, 300);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // Dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  // Build filter object - only include non-empty values
  const filter = useMemo(() => {
    const f: Record<string, string | undefined> = {};
    if (debouncedSearch && debouncedSearch.trim().length > 0) {
      f.search = debouncedSearch.trim();
    }
    if (statusFilter) {
      f.status = statusFilter;
    }
    if (roleFilter) {
      f.role = roleFilter;
    }
    return Object.keys(f).length > 0 ? f : undefined;
  }, [debouncedSearch, statusFilter, roleFilter]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, roleFilter]);

  // Queries
  const { data: statsData, loading: statsLoading, refetch: refetchStats } = useQuery(GET_ADMIN_USER_GLOBAL_STATS, {
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_USERS, {
    variables: {
      filter,
      page: page + 1,
      pageSize: rowsPerPage,
    },
    fetchPolicy: 'network-only', // Always fetch fresh data for filtered results
  });

  // Mutations
  const [suspendUser, { loading: suspending }] = useMutation(SUSPEND_USER);
  const [reactivateUser, { loading: reactivating }] = useMutation(REACTIVATE_USER);
  const [setVerifiedBadge, { loading: settingBadge }] = useMutation(SET_VERIFIED_BADGE);
  const [setUserRole, { loading: settingRole }] = useMutation(SET_USER_ROLE);

  // Role change dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<string>('');

  const stats: GlobalStats | null = statsData?.adminUserGlobalStats;
  const users: AdminUser[] = data?.adminUsers?.items || [];
  const total = data?.adminUsers?.total || 0;

  // Handlers
  const handleRefresh = () => {
    refetch();
    refetchStats();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;
    try {
      await suspendUser({
        variables: { userId: selectedUser.id, reason: suspendReason },
      });
      notify('Utilisateur suspendu', { type: 'success' });
      setSuspendDialogOpen(false);
      setSuspendReason('');
      handleRefresh();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
    handleMenuClose();
  };

  const handleReactivate = async () => {
    if (!selectedUser) return;
    try {
      await reactivateUser({ variables: { userId: selectedUser.id } });
      notify('Utilisateur réactivé', { type: 'success' });
      handleRefresh();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
    handleMenuClose();
  };

  const handleToggleVerified = async () => {
    if (!selectedUser) return;
    try {
      await setVerifiedBadge({
        variables: { userId: selectedUser.id, verified: !selectedUser.verifiedBadge },
      });
      notify(selectedUser.verifiedBadge ? 'Badge retiré' : 'Badge attribué', { type: 'success' });
      handleRefresh();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
    handleMenuClose();
  };

  const handleOpenRoleDialog = () => {
    if (selectedUser) {
      setNewRole(selectedUser.role);
      setRoleDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await setUserRole({
        variables: { userId: selectedUser.id, role: newRole },
      });
      notify(`Rôle changé en ${newRole}`, { type: 'success' });
      setRoleDialogOpen(false);
      handleRefresh();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const handleEditProfile = () => {
    if (selectedUser) {
      navigate(`/users/${selectedUser.id}/show`);
    }
    handleMenuClose();
  };

  const handleRowClick = (userId: string) => {
    navigate(`/users/${userId}/show`);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Utilisateurs" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Gestion des utilisateurs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les comptes utilisateurs de la plateforme
          </Typography>
        </Box>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total utilisateurs"
            value={stats?.total || 0}
            icon={<PeopleIcon />}
            color="#6366F1"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Comptes actifs"
            value={stats?.active || 0}
            icon={<CheckCircleIcon />}
            color="#10B981"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Comptes vérifiés"
            value={stats?.verified || 0}
            icon={<VerifiedIcon />}
            color="#3B82F6"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Nouveaux ce mois"
            value={stats?.newThisMonth || 0}
            icon={<TrendingUpIcon />}
            color="#F59E0B"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher par nom, email, slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="ACTIVE">Actif</MenuItem>
                <MenuItem value="SUSPENDED">Suspendu</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rôle</InputLabel>
              <Select
                value={roleFilter}
                label="Rôle"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="USER">Utilisateur</MenuItem>
                <MenuItem value="MODERATOR">Modérateur</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur: {error.message}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Entreprise</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Cartes</TableCell>
              <TableCell>Inscription</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucun utilisateur trouvé</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(user.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={user.avatarUrl || undefined} sx={{ width: 40, height: 40 }}>
                        {user.displayName?.charAt(0) || user.email?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {user.displayName || `${user.firstName} ${user.lastName}` || '-'}
                          </Typography>
                          {user.verifiedBadge && (
                            <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          )}
                          {user.isPremium && (
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          @{user.username || user.slug || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.company || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.subscription?.plan || 'FREE'}
                      size="small"
                      color={user.subscription?.plan === 'FREE' ? 'default' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.accountStatus}
                      size="small"
                      sx={{
                        bgcolor: `${STATUS_COLORS[user.accountStatus]}20`,
                        color: STATUS_COLORS[user.accountStatus],
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={{
                        bgcolor: `${ROLE_COLORS[user.role]}20`,
                        color: ROLE_COLORS[user.role],
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.cardsCount}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Voir profil public">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${PUBLIC_APP_URL}/profile/${user.slug}`, '_blank');
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, user);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Par page"
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditProfile}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Voir / Éditer le profil</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenRoleDialog} disabled={settingRole}>
          <ListItemIcon><SwapHorizIcon fontSize="small" color="secondary" /></ListItemIcon>
          <ListItemText>Changer le rôle</ListItemText>
        </MenuItem>
        {selectedUser?.accountStatus === 'ACTIVE' ? (
          <MenuItem onClick={() => { handleMenuClose(); setSuspendDialogOpen(true); }}>
            <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Suspendre</ListItemText>
          </MenuItem>
        ) : selectedUser?.accountStatus === 'SUSPENDED' ? (
          <MenuItem onClick={handleReactivate} disabled={reactivating}>
            <ListItemIcon><CheckCircleIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Réactiver</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleToggleVerified} disabled={settingBadge}>
          <ListItemIcon><VerifiedIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{selectedUser?.verifiedBadge ? 'Retirer badge' : 'Attribuer badge'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)}>
        <DialogTitle>Suspendre l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            L'utilisateur ne pourra plus se connecter à son compte.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Raison (optionnel)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="Ex: Violation des conditions d'utilisation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSuspend}
            disabled={suspending}
          >
            {suspending ? <CircularProgress size={20} /> : 'Suspendre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Changer le rôle de l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Utilisateur: <strong>{selectedUser?.displayName || selectedUser?.email}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Nouveau rôle</InputLabel>
            <Select
              value={newRole}
              label="Nouveau rôle"
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value="USER">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" sx={{ color: ROLE_COLORS.USER }} />
                  Utilisateur
                </Box>
              </MenuItem>
              <MenuItem value="MODERATOR">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ color: ROLE_COLORS.MODERATOR }} />
                  Modérateur
                </Box>
              </MenuItem>
              <MenuItem value="ADMIN">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ color: ROLE_COLORS.ADMIN }} />
                  Administrateur
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          {newRole === 'ADMIN' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Attention: Ce rôle donne accès à toutes les fonctionnalités d'administration.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleChangeRole}
            disabled={settingRole || newRole === selectedUser?.role}
          >
            {settingRole ? <CircularProgress size={20} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserList;
